import {
  ChipExpression,
  ExpertChipExpression,
  GroupChipExpression,
  SimpleChipExpression
} from '@nrwl/query-emitter';
import { Operator } from '@nrwl/smart-search';
import { find, join, toUpper } from 'lodash';

export class GrammarHelper {
  static normalizeSimpleChipExpression(expr) {
    const crntOperands = expr.operands;
    if (Array.isArray(crntOperands)) {
      // no changes
      return expr;
    }
    if (crntOperands) {
      expr.operands = [crntOperands];
    } else {
      expr.operands = [];
    }
    return expr;
  }

  static normalizeExpertChipExpression(expr) {
    // right now, simple and expert chip expressions match. But it is highly probably they will diverge soon.
    return GrammarHelper.normalizeSimpleChipExpression(expr);
  }

  static allExpressionFieldsPopulated(parsedExpression): boolean {
    if (!parsedExpression) {
      return false;
    }
    if (parsedExpression.isGroup) {
      if (!parsedExpression.group || parsedExpression.group.length === 0) {
        return false;
      }
      let valid = true;
      for (const chip of parsedExpression.group ? parsedExpression.group : []) {
        valid = valid && this.allExpressionFieldsPopulated(chip);
      }
      return valid;
    }

    if (!parsedExpression.keyword) {
      return false;
    }
    if (!parsedExpression.operator) {
      return false;
    }
    if (!parsedExpression.operands) {
      return false;
    }
    return parsedExpression.operands.length !== 0;
  }

  static toExpression(chips: ChipExpression[]): string {
    if (!chips) {
      return '';
    }
    // current implementation covers just part of grammar.
    let sql = '';
    chips.forEach(expression => {
      if (expression.isGroup) {
        (expression as GroupChipExpression).group[0]['logicalOp'] = expression['logicalOp'];
        (expression as GroupChipExpression).group.forEach((member, index) => {
          if (expression instanceof Operator) {
            sql += ` ${member.keyword} `;
          } else {
            sql += GrammarHelper.prependLogicalOp(member);
            if (index === 0) {
              sql += ' (';
            }
            sql += GrammarHelper.parseExpression(member);
            if (index === (expression as GroupChipExpression).group.length - 1) {
              sql += ') ';
            }
          }
        });
      } else {
        if (expression instanceof Operator) {
          sql += ` ${expression.keyword} `;
        } else {
          sql += GrammarHelper.prependLogicalOp(expression as SimpleChipExpression);
          sql += GrammarHelper.parseExpression(expression as SimpleChipExpression);
        }
      }
    });
    return sql;
  }

  static toAtaBreadcrumbExp(chips: SimpleChipExpression[]): string {
    const app = find(chips, item => item.keyword === 'app');
    const ip1 = find(chips, item => item.keyword === 'ip1');
    const ip2 = find(chips, item => item.keyword === 'ip2');

    if (ip1 && ip2) {
      return (
        ` >  ${ip1.operands[0]} to ${ip2.operands[0]}` +
        (app && app.operands ? ` / ${app.operands}` : '')
      );
    } else if (ip1 && !ip2) {
      return ` >  ${ip1.operands[0]}` + (app && app.operands ? ` / ${app.operands}` : '');
    } else if (!ip1 && ip2) {
      return ` >  ${ip2.operands[0]}` + (app && app.operands ? ` / ${app.operands}` : '');
    }

    return chips.map(chip => `${chip.keyword} ${chip.operands.join(', ')}`).join(' and ');
  }

  static parseExpression(exp: SimpleChipExpression) {
    let sql = '';
    sql += exp.keyword;
    switch (toUpper(exp.operator)) {
      case '=':
      case '!=':
      case '~':
      case '!~':
        if (exp.operands.length > 1) {
          sql += exp.operator === '!=' ? ' NOT IN (' : ' IN (';
          sql += GrammarHelper.parseOperands(exp.operands);
          sql += ')';
          return sql;
        } else {
          sql += ` ${exp.operator} `;
          return (sql += GrammarHelper.parseOperands(exp.operands));
        }
      case 'IS': {
        sql += ' IS ';
        sql += GrammarHelper.parseIsOperatorOperands(exp.operands);
        return sql;
      }
      case 'IS NOT': {
        // assumption is there is precisely one operand. Otherwise operator is not correct
        sql += ' IS NOT ';
        sql += GrammarHelper.parseIsOperatorOperands(exp.operands);
        return sql;
      }
      case ':':
      case 'IN': {
        sql += ' IN (';
        sql += GrammarHelper.parseOperands(exp.operands);
        sql += ')';
        return sql;
      }
      case 'NOT IN': {
        sql += ' NOT IN (';
        sql += GrammarHelper.parseOperands(exp.operands);
        sql += ')';
        return sql;
      }
      case '>=':
      case '<=': {
        sql += ` ${exp.operator} `;
        sql += GrammarHelper.parseOperands(exp.operands);
        return sql;
      }
      default: {
        console.error('not yet implemented:', exp.operator);
      }
    }
    return sql;
  }

  static parseIsOperatorOperands(operands: string[]) {
    if (operands.length > 0) {
      if (toUpper(operands[0]) === 'NULL') {
        return 'NULL';
      }
      return this.parseOperands(operands);
    }
    return 'NULL';
  }

  static parseOperands(operands: string[]) {
    const ops = [];
    operands.forEach(operand => {
      if (typeof operand === 'number') {
        ops.push('' + operand);
      } else {
        // strip leading and ending ""
        const strippedOperand = operand.replace(/^"(.*)"$/, '$1');
        // being able to use identifiers on the right hand side
        if (strippedOperand.startsWith('__')) {
          ops.push(strippedOperand.replace(/^__/, ''));
        } else {
          ops.push(`'${strippedOperand}'`);
        }
      }
    });
    if (ops.length === 1) {
      return ops[0];
    }
    return join(ops, ', ');
  }

  static prependLogicalOp(expression: SimpleChipExpression | ExpertChipExpression) {
    if (
      typeof (expression as ExpertChipExpression).logicalOp !== 'undefined' &&
      (expression as ExpertChipExpression).logicalOp
    ) {
      return ` ${(expression as ExpertChipExpression).logicalOp} `;
    }
    return '';
  }

  static toBreadcrumbExp(chips: SimpleChipExpression[]): string {
    return chips.map(chip => `${chip.keyword} ${chip.operands.join(', ')}`).join(' and ');
  }
}
